import React, { RefObject } from 'react';
import { withTranslation, WithTranslation } from '@wix/wix-i18n-config';
import Header from '../Header';
import FAQ from '../FAQ';
import Terms from '../Terms';
import Footer from '../Footer';
import * as windowUtils from '../../services/window-utils';
import { ERR_MSG_NOT_AUTH, TECH_ERRORS } from '../../constants';
import {
  getQueryParam,
  isEntry,
  isLocalCyrillic,
  isMobileDevice,
  navigateToUrl,
} from '../../services/window-utils';
import { ClientOffering } from '../../services/client-offering';
import { TypeSelectedOffer } from '../../types/general-types';
import { biLogger, fedopsLogger, createData } from '../../globals';
import ErrorComponent from '../ErrorComponent';
import MobileApp from '../Mobile/MobileApp/MobileApp';
import MobileThanks from '../Mobile/MobileThanks/MobileThanks';
import Thanks from '../Thanks/Thanks';
import { Loader } from 'wix-style-react';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import SaleHeader from '../Sale/SaleHeader/SaleHeader';
import SaleBanner from '../Sale/SaleBanner/SaleBanner';
import s from './App.scss';
import CompareTable from '../CompareTable/CompareTable';
import CompareTableContent_LEGACY from '../../CompareTableContent_LEGACY.json';
import CompareTableContent from '../../CompareTableContent.json';
import AlwaysFree from '../AllInclude/AllInclude';
import Highlights from '../Highlights/Highlights';

const scrollNeededToSendBi = 1300;

export interface AppProps extends WithTranslation, InjectedExperimentsProps {
  isOnThanks: boolean;
  userData: any;
}

interface AppState {
  TemplateIntro: Function;
  loaded: boolean;
  error: string | null;
  data: ClientOffering | null;
  isWixPremiumSite: boolean;
  isMobile: boolean;
  shouldRenderApp: boolean;
}

class App extends React.Component<AppProps, AppState> {
  benefitRef: RefObject<HTMLElement> = React.createRef();
  comapreRef: RefObject<HTMLDivElement> = React.createRef();
  scrollToBenefits = () => windowUtils.scrollToRef(this.benefitRef);

  constructor(props: AppProps) {
    super(props);
    this.state = {
      TemplateIntro: () => null,
      loaded: false,
      error: null,
      data: null,
      isWixPremiumSite: false,
      isMobile: false,
      shouldRenderApp: false,
    };
  }

  selectedOffer: TypeSelectedOffer = {};
  urlParams = getQueryParam();
  msid =
    this.urlParams.get('metaSiteId') ||
    this.urlParams.get('__metaSiteId') ||
    '';

  scrollToCompare = () => {
    windowUtils.scrollToRef(this.comapreRef);
    biLogger.buttonClicked({ msid: this.msid, button_name: 'compare' });
  };

  handleScrollBi = () => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > scrollNeededToSendBi) {
      biLogger.fistTimeScrollToEndTable();
      window.removeEventListener('scroll', this.handleScrollBi);
    }
  };

  async componentDidMount() {
    const isMobile = isMobileDevice();
    this.setState({ isMobile });

    if (!isMobile) {
      biLogger.init();
      window.addEventListener('scroll', this.handleScrollBi, { passive: true });

      try {
        const resellerURL = window.__resellerURL__;
        if (resellerURL !== undefined && resellerURL !== '') {
          window.location.replace(resellerURL);
        } else {
          this.setState({ shouldRenderApp: true });
        }
      } catch (e) {
        console.error('Failed to extract resellerURL');
      }
    }

    const { default: TemplateIntro } = await import(
      '@wix/yoshi-template-intro'
    );
    this.setState({ TemplateIntro });

    if (!this.msid) {
      const error = TECH_ERRORS.MISSING_MSID;
      return this.setState({ loaded: true, error });
    }

    this.selectedOffer.metaSiteId = this.msid;

    try {
      if (!this.props.userData || this.props.userData.status !== 'success') {
        return this.setState({
          loaded: true,
          error: TECH_ERRORS.USER_DATA_FETCH_FALILED,
        });
      }

      const isPermitted = this.props.userData.isUserPermitted;

      if (!isPermitted) {
        return this.setState({
          loaded: true,
          error: ERR_MSG_NOT_AUTH,
        });
      }

      const isWixPremiumSite = this.props.userData.isUserPremium;
      const clientOffering = this.props.userData.clientOffering;
      const experiments = {
        showAscendMonetizationChanges: this.props.experiments.enabled(
          'specs.so.app.NewAscendMonetization',
        ),
      };
      const data = createData(clientOffering, experiments);
      this.setState({ loaded: true, data, isWixPremiumSite });
    } catch (e) {
      const isAscendPackagePickerOn = this.props.experiments.enabled(
        'specs.ascend.AccessPackagePicker',
      );
      if (!isAscendPackagePickerOn) {
        const param = window.location.search;
        const appMarketURL = `https://manage.wix.com/apps/upgrade/ee21fe60-48c5-45e9-95f4-6ca8f9b1c9d9${param}`;
        navigateToUrl(appMarketURL);
      } else {
        this.setState({ loaded: true, error: e as string });
      }
    }
  }

  componentDidCatch = (error: Error) => {
    this.setState({ loaded: true, error: error.message });
  };

  renderLoader() {
    return (
      <div className={s.loader}>
        <Loader
          size="large"
          text={this.props.t('app.loader')}
          data-hook="loading"
        />
      </div>
    );
  }

  renderDesktop(
    clientOffering: ClientOffering,
    selectedOffer: TypeSelectedOffer,
  ) {
    const isOnThanks = this.props.isOnThanks;
    const invoiceNum = getQueryParam().get('invoiceId');
    const isSaleOn = clientOffering.isSaleOn();
    const isCouponBannerOn = this.props.experiments.enabled(
      'specs.ascend.SaleCoupon',
    );
    const showFormsExperience =
      this.props.experiments.enabled(
        'specs.ascend.PackagePickerFormsEntryPoint',
      ) && isEntry.forms();

    const showAscendMonetizationChanges = this.props.experiments.enabled(
      'specs.so.app.NewAscendMonetization',
    );

    return this.state.shouldRenderApp ? (
      isOnThanks ? (
        <Thanks
          invoiceNum={invoiceNum}
          msid={this.msid}
          data={clientOffering}
        />
      ) : (
        <div className={s.root} data-hook="app-root">
          {isSaleOn && <SaleHeader />}
          <Header
            isWixPremiumSite={this.state.isWixPremiumSite}
            msid={this.msid}
          />
          {isCouponBannerOn && <SaleBanner />}
          <Highlights
            onScrollToCompare={this.scrollToCompare}
            data={clientOffering}
            selectedOffer={selectedOffer}
          />
          <div>
            <CompareTable
              data={clientOffering}
              selectedOffer={selectedOffer}
              tableContent={
                showAscendMonetizationChanges
                  ? CompareTableContent
                  : CompareTableContent_LEGACY
              }
              scrollRef={this.comapreRef}
              isFormsEntryPoint={showFormsExperience}
            />
            <AlwaysFree />
            <Terms isPriceIncludedTax={!!clientOffering.currency.taxType} />
            <FAQ />
            <Footer />
          </div>
        </div>
      )
    ) : (
      this.renderLoader()
    );
  }

  renderMobile = (
    clientOffering: ClientOffering,
    selectedOffer: TypeSelectedOffer,
  ) => {
    const isOnThanks = this.props.isOnThanks;
    return isOnThanks ? (
      <MobileThanks />
    ) : (
      <MobileApp clientOffering={clientOffering} metaSiteId={this.msid} />
    );
  };

  render() {
    isLocalCyrillic()
      ? require('../font_Cyrillic.scss')
      : require('../font.scss');

    if (this.state.error) {
      return (
        <ErrorComponent
          data-hook="app-error"
          customErrorText={this.state.error || ''}
          hostname="www.wix.com"
          isMobile={this.state.isMobile}
        />
      );
    }

    if (!this.state.loaded) {
      return this.renderLoader();
    }

    const clientOffering: ClientOffering | null = this.state.data;

    fedopsLogger.appLoaded();

    if (!this.props.isOnThanks && !this.state.isMobile) {
      biLogger.ppOpen();
    }

    if (clientOffering && !this.state.error) {
      const selectedOffer = {
        ...this.selectedOffer,
        currentProduct: clientOffering.currentProduct,
      };

      return this.state.isMobile
        ? this.renderMobile(clientOffering, selectedOffer)
        : this.renderDesktop(clientOffering, selectedOffer);
    }
  }
}

export default withTranslation()(withExperiments(App));
